import { ColDef, ICellEditorParams } from "ag-grid-community";
import _ from "lodash";
import moment from "moment";
import ActionsApi, { ActionsApi as IActionsApi } from "../../../../../services/api/v2/actions/Actions.api";
import { BaseGridColumnBuilder } from "../../../../../apps/grids/builder/directorBuilder/grids/base/BaseGridColumnBuilder";
import GridToastService from "../../../../../services/local/gridToastService/GridToastService";
import ProgressIndicatorModel from "../../../../../components/widgets/ProgressIndicator/ProgressIndicator_model";
import I18n from "../../../../../core/localization/I18n";
import {
  PROGRESS_STATUS_COLUMN_CONFIG,
  PROGRESS_STATUS_FILTER_CONFIG
} from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/AutocompleteColumn/progressStatus/ProgressStatusColumn_config";
import { AutocompleteColumnBuilder } from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/AutocompleteColumn/AutocompleteColumn_builder";
import { ActionField, defaultDateTimeString, Enums } from "../../../../../enums";
import { laterDateComparator } from "../../../../../core/grids/GridHelpers";
import {
  OWNER_NAME_COLUMN_CONFIG,
  OWNER_NAME_FILTER_CONFIG
} from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/AutocompleteColumn/ownerName/OwnerNameColumn_config";
import { SimpleTextColumnBuilder } from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/SimpleTextColumn/SimpleTextColumn_builder";
import { SIMPLE_TEXT_COLUMN_CONFIG } from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/SimpleTextColumn/SimpleTextColumn_config";
import { BASE_FILTER_CONFIG } from "../../../../../apps/grids/builder/directorBuilder/columns/baseColumn/BaseColumn_config";
import { RagStatusColumnBuilder } from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/RagStatusColumn/RagStatusColumn_builder";
import { RAGStatusEditor } from "../../../../../core/grids/editors/editorRAGStatus";
import { ProgressStatus } from "@flightpath/coreui/dist/ui/ProgressStatus";
import { RagStatus } from "@flightpath/coreui/dist/ui/RagStatus";
import {
  mapProgressStatuses,
  RAG_STATUS_FILTER_CONFIG
} from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/RagStatusColumn/RagStatusColumn_config";
import {
  ActionColDefFieldNamesEnum,
  CommonColDefFieldNamesEnum
} from "../../../../../apps/grids/builder/enums/AgGridColDefFieldNameEnum";
import { RefNumberColumnBuilder } from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/RefNumberColumn/RefNumberColumn_builder";
import {
  REF_NUMBER_COLUMN_CONFIG,
  REF_NUMBER_FILTER_CONFIG
} from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/RefNumberColumn/RefNumberColumn_config";
import { NameColumnBuilder } from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/NameColumn/NameColumn_builder";
import {
  NAME_COLUMN_CONFIG,
  NAME_FILTER_CONFIG
} from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/NameColumn/NameColumn_config";
import {
  AUTOCOMPLETE_COLUMN_CONFIG,
  AUTOCOMPLETE_FILTER_CONFIG
} from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/AutocompleteColumn/AutocompleteColumn_config";
import { DateColumnBuilder } from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/DateColumn/DateColumn_builder";
import {
  DATE_COLUMN_CONFIG,
  DATE_FILTER_CONFIG
} from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/DateColumn/DateColumn_config";
import { ChangePages } from "../../../../../apps/change/routes/pages";
export interface ActionsGridDashboardColumnBuilderProps {
  canEdit: boolean;
  organisationId: number;
  projectId: number;
  userCanViewActions: boolean;
  columns: FP.Entities.IColumnDef[];
  projectTeamMembers: any[];
  onFieldUpdate: () => void;
}

export class ActionsGridDashboardColumnBuilder extends BaseGridColumnBuilder {
  gridColumns: Dictionary<ColDef>;
  gridToastService = GridToastService;
  httpProgress = ProgressIndicatorModel;
  gridProps: ActionsGridDashboardColumnBuilderProps;
  actionsProvider: IActionsApi = ActionsApi;
  columnDefs: Dictionary<() => ColDef>;
  organisationId: number;
  projectId: number;
  projectTeamMembers: any[];
  reloadData: () => void;

  constructor(gridProps: ActionsGridDashboardColumnBuilderProps) {
    super(ActionsApi.updateField, gridProps.organisationId, gridProps.projectId, gridProps.canEdit);
    this.gridProps = gridProps;
    this.organisationId = gridProps.organisationId;
    this.projectId = gridProps.projectId;
    this.projectTeamMembers = gridProps.projectTeamMembers;
    this.reloadData = gridProps.onFieldUpdate;
    this.init();
  }

  private init = () => {
    this.columnDefs = {
      [ActionColDefFieldNamesEnum.RagStatus]: () => this.buildRagStatusColumn(),
      [CommonColDefFieldNamesEnum.RefNumber]: () => this.buildRefNumberColumn(),
      [CommonColDefFieldNamesEnum.Name]: () => this.buildNameColumn(),
      [CommonColDefFieldNamesEnum.ProjectName]: () => this.buildProjectNameColumn(),
      [CommonColDefFieldNamesEnum.ProgrammeName]: () => this.buildProgrammeNameColumn(),
      [CommonColDefFieldNamesEnum.Owner]: () => this.buildOwnerColumn(),
      [CommonColDefFieldNamesEnum.ProgressStatus]: () => this.buildProgressStatusColumn(),
      [ActionColDefFieldNamesEnum.AssignedTo]: () => this.buildAssignedToColumn(),
      [CommonColDefFieldNamesEnum.StartDate]: () => this.buildStartDateColumn(),
      [CommonColDefFieldNamesEnum.EndDate]: () => this.buildEndDateColumn()
    };
  };

  generateColumnDefs = (): ColDef[] => {
    let res: ColDef[] = [];

    if (this.gridProps.columns?.length === 0) {
      return _.map(this.columnDefs, e => {
        return e();
      });
    }
    this.gridProps.columns.forEach(e => {
      if (e.isCore) {
        let col = this.columnDefs[e.colName];
        if (typeof col === "function") {
          res.push(col());
        } else {
          throw new Error(`Typeof columnDef with name "${e.colName}" is not a function`);
        }
      } else {
        let col = this.buildDynamicColumn(e, this.gridProps.canEdit);
        res.push(col);
      }
    });

    return res;
  };

  buildRefNumberColumn = () => {
    let model = new RefNumberColumnBuilder()
      .setColumnOptions(REF_NUMBER_COLUMN_CONFIG({ headerName: I18n.t("grids.refNo") }))
      .withLinkCell(
        (actionId, projectId) =>
          ChangePages.actions.extendedView.generateLink(this.organisationId, projectId || this.projectId, actionId),
        CommonColDefFieldNamesEnum.RefNumber
      )
      .setFilterOptions(REF_NUMBER_FILTER_CONFIG);

    return model.generateColumnOptions();
  };
  buildRagStatusColumn = () => {
    let model = new RagStatusColumnBuilder()
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .setColumnOptions({
        field: ActionColDefFieldNamesEnum.RagStatus,
        headerName: I18n.t("forms.ragStatus"),
        width: 100,
        cellEditorSelector: () => ({ component: RAGStatusEditor, popup: true, popupPosition: "under" }),
        cellRenderer: columnProps => {
          let obj = columnProps.data;
          return obj.progressStatus === Enums.ProgressStatus.COMPLETED ? (
            <ProgressStatus state={obj.progressStatus} />
          ) : (
            <RagStatus state={obj.ragStatus} />
          );
        },
        comparator: (valueA, valueB, nodeA, nodeB) => {
          if (nodeA.data.progressStatus === Enums.ProgressStatus.COMPLETED) valueA = 4;
          if (nodeB.data.progressStatus === Enums.ProgressStatus.COMPLETED) valueB = 4;

          return valueA - valueB;
        },
        filterValueGetter: params => {
          return params.data.progressStatus === Enums.ProgressStatus.COMPLETED ? 4 : params.data.ragStatus;
        },
        cellDataType: "any"
      })
      .setValueSetter(params => {
        if (!params.newValue) return false;

        this.updateRagStatus(+params.data.id, params.newValue, params.data.projectId);
        return true;
      })
      .setFilterOptions(RAG_STATUS_FILTER_CONFIG);

    return model.generateColumnOptions();
  };
  buildNameColumn = () => {
    let model = new NameColumnBuilder()
      .setColumnOptions(NAME_COLUMN_CONFIG({ headerName: "Name", pinned: false }))
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .setFilterOptions(NAME_FILTER_CONFIG);

    if (this.gridProps.canEdit) {
      // make cell editable
      model.createValueSetterWithProjectId(this.updateName);
    }

    return model.generateColumnOptions();
  };
  buildProjectNameColumn = () => {
    let model = new SimpleTextColumnBuilder({
      field: CommonColDefFieldNamesEnum.ProjectName,
      headerName: I18n.t("grids.project")
    })
      .setColumnOptions(
        SIMPLE_TEXT_COLUMN_CONFIG({
          field: CommonColDefFieldNamesEnum.ProjectName,
          headerName: I18n.t("grids.project")
        })
      )
      .makeSelectable(this.canEdit)
      .makeEditable(false)
      .makeReadOnly(!this.canEdit)
      .setCellDataType("text")
      .setFilterOptions(BASE_FILTER_CONFIG);

    if (this.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };
  buildProgrammeNameColumn = () => {
    let model = new SimpleTextColumnBuilder({
      field: CommonColDefFieldNamesEnum.ProgrammeName,
      headerName: I18n.t("grids.programme")
    })
      .setColumnOptions(
        SIMPLE_TEXT_COLUMN_CONFIG({
          field: CommonColDefFieldNamesEnum.ProgrammeName,
          headerName: I18n.t("grids.programme")
        })
      )
      .makeSelectable(this.canEdit)
      .makeEditable(false)
      .makeReadOnly(!this.canEdit)
      .setCellDataType("text")
      .setFilterOptions(BASE_FILTER_CONFIG);

    if (this.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };

  buildProgressStatusColumn = () => {
    const progressStatuses = mapProgressStatuses();
    let model = new AutocompleteColumnBuilder()
      .setColumnOptions(AUTOCOMPLETE_COLUMN_CONFIG({ field: "progressStatus", headerName: I18n.t("grids.status") }))
      .setFilterOptions({
        ...AUTOCOMPLETE_FILTER_CONFIG,
        ...PROGRESS_STATUS_FILTER_CONFIG
      })
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .withCellEditor()
      .setValueSetter(params => {
        if (!params.newValue) return false;

        params.data.progressStatus = +params.newValue.key;
        this.updateProgressStatus(params.data.id, params.newValue.key, params.data.projectId);

        return true;
      })
      .withCellRenderer(params => {
        if (!params.data.progressStatus || !progressStatuses) return null;
        const progress = progressStatuses.find(e => e.key === params.data.progressStatus + "");

        if (!progress) return "";
        return <>{progress.label}</>;
      })
      .setColumnOptions(PROGRESS_STATUS_COLUMN_CONFIG(progressStatuses));

    return model.generateColumnOptions();
  };
  buildAssignedToColumn = () => {
    let model = new AutocompleteColumnBuilder()
      .setColumnOptions({
        field: ActionColDefFieldNamesEnum.AssignedTo,
        headerName: I18n.t("grids.assignedTo")
      })
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .withCellRenderer(e => <div>{e.data.assignedToId > 0 ? e.data.assignedToName : ""}</div>)
      .setValueGetter(e => {
        if (!e.data.assignedToName) return;
        return `${e.data.assignedToName ?? ""}`;
      })
      .setValueSetter(params => {
        if (!params.newValue) return false;

        params.data.assignedToId = params.newValue.id;
        params.data.assignedToName = `${params.newValue.firstName} ${params.newValue.lastName}`;

        this.updateAssignedTo(+params.data.id, params.newValue.id, params.data.projectId);
        return true;
      })
      .withCellEditor()
      .setColumnOptions({
        cellEditorParams: {
          field: ActionColDefFieldNamesEnum.AssignedTo,
          getValueLabel: (ee: any) => {
            if (!this.projectTeamMembers) return "";

            const user = this.projectTeamMembers.find(e => e.id === ee.id);
            if (!user) return "";
            return user.firstName + " " + user.lastName;
          },
          options: this.projectTeamMembers ?? [],
          allowFreeText: true,
          isPerson: true,
          filterFn: (items, query) => {
            const lowerQuery = query.toLowerCase();
            return items.filter((item: FP.Entities.IUser) => {
              const lowerName = `${item.firstName} ${item.lastName}`.toLowerCase();
              const lowerEmail = item.email.toLowerCase();
              return lowerName.indexOf(lowerQuery) > -1 || lowerEmail.indexOf(lowerQuery) > -1;
            });
          }
        }
      })
      .setFilterOptions({
        ...AUTOCOMPLETE_FILTER_CONFIG
      });

    if (this.gridProps.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };

  buildStartDateColumn = () => {
    let model = new DateColumnBuilder()
      .setColumnOptions(
        DATE_COLUMN_CONFIG({
          field: CommonColDefFieldNamesEnum.StartDate,
          headerName: I18n.t("grids.startDate")
        })
      )
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .withComparator()
      .withCellEditor(CommonColDefFieldNamesEnum.StartDate, "")
      .setValueFormatter(CommonColDefFieldNamesEnum.StartDate)
      .setValueGetterByFieldFn(CommonColDefFieldNamesEnum.StartDate)
      .setFilterOptions(DATE_FILTER_CONFIG)
      .setValueSetter(params => {
        return true;
      })
      .setColumnOptions({
        cellEditorParams: {
          field: CommonColDefFieldNamesEnum.StartDate,
          defaultDate: moment(),
          onDatepickerClick: (params: ICellEditorParams, e) => {
            if (!e) {
              params.data[CommonColDefFieldNamesEnum.StartDate] = defaultDateTimeString;
              return params.data[CommonColDefFieldNamesEnum.StartDate];
            }

            let startDate = moment(e);
            let endDate =
              params.data[CommonColDefFieldNamesEnum.EndDate] &&
              params.data[CommonColDefFieldNamesEnum.EndDate] !== defaultDateTimeString &&
              moment(params.data[CommonColDefFieldNamesEnum.EndDate]);

            if (!(startDate === endDate) && (!endDate || endDate > startDate)) {
              this.updateStartDate(params.data.id, startDate.toDate(), params.data.projectId);
              return e;
            }
            return params.data[CommonColDefFieldNamesEnum.StartDate];
          }
        }
      });

    if (this.gridProps.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };

  buildEndDateColumn = () => {
    let model = new DateColumnBuilder()
      .setColumnOptions(
        DATE_COLUMN_CONFIG({ field: CommonColDefFieldNamesEnum.EndDate, headerName: I18n.t("grids.endDate") })
      )
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .withComparator()
      .withCellEditor(CommonColDefFieldNamesEnum.EndDate, "")
      .setValueFormatter(CommonColDefFieldNamesEnum.EndDate)
      .setValueGetterByFieldFn(CommonColDefFieldNamesEnum.EndDate)
      .setFilterOptions(DATE_FILTER_CONFIG)
      .setValueSetter(params => {
        return true;
      })
      .setColumnOptions({
        cellEditorParams: {
          field: CommonColDefFieldNamesEnum.EndDate,
          defaultDate: moment(),
          onDatepickerClick: (params: ICellEditorParams, e) => {
            if (!e) {
              params.data[CommonColDefFieldNamesEnum.EndDate] = defaultDateTimeString;
              return params.data[CommonColDefFieldNamesEnum.EndDate];
            }

            let endDate = moment(e);
            let startDate =
              params.data[CommonColDefFieldNamesEnum.StartDate] &&
              params.data[CommonColDefFieldNamesEnum.StartDate] !== defaultDateTimeString &&
              moment(params.data[CommonColDefFieldNamesEnum.StartDate]);

            if (
              !(endDate === startDate) &&
              (!startDate || laterDateComparator(endDate.toDate(), startDate.toDate()) === true)
            ) {
              this.updateEndDate(params.data.id, endDate.toDate(), params.data.projectId);
              return e;
            }
            return params.data[CommonColDefFieldNamesEnum.EndDate];
          }
        }
      });

    if (this.gridProps.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };

  buildOwnerColumn = () => {
    let model = new AutocompleteColumnBuilder()
      .setColumnOptions({
        ...AUTOCOMPLETE_COLUMN_CONFIG({ field: CommonColDefFieldNamesEnum.Owner, headerName: I18n.t("grids.owner") }),
        ...OWNER_NAME_COLUMN_CONFIG(CommonColDefFieldNamesEnum.Owner, this.projectTeamMembers)
      })
      .setFilterOptions({
        ...AUTOCOMPLETE_FILTER_CONFIG,
        ...OWNER_NAME_FILTER_CONFIG
      })
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .withCellEditor()
      .setValueGetter(e => {
        if (!e.data[CommonColDefFieldNamesEnum.Owner]) return;
        return `${e.data[CommonColDefFieldNamesEnum.Owner] ?? ""}`;
      })
      .setValueSetter(params => {
        if (!params.newValue) return false;

        params.data.ownerId = params.newValue.id;
        params.data[CommonColDefFieldNamesEnum.Owner] = `${params.newValue.firstName} ${params.newValue.lastName}`;

        this.updateOwnerName(params.data.id, params.newValue.id, params.data.projectId);
        return true;
      });

    if (this.gridProps.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };

  updateName = async (entityId: number, text: string, projectId: number) => {
    await this.updateTextField(ActionField.name, entityId, text, projectId);
    await this.reloadData();
  };
  updateRagStatus = async (entityId: number, data: any, projectId: number) => {
    await this.updateIdField(ActionField.ragStatus, entityId, data, projectId);
    await this.reloadData();
  };
  updateStartDate = async (entityId: number, date: Date, projectId: number) => {
    await this.updateDateField(ActionField.startDate, entityId, date, projectId);
    await this.reloadData();
  };
  updateEndDate = async (entityId: number, date: Date, projectId: number) => {
    await this.updateDateField(ActionField.endDate, entityId, date, projectId);
    await this.reloadData();
  };
  updateAssignedTo = async (entityId: number, data: any, projectId: number) => {
    await this.updateIdField(ActionField.assignedTo, entityId, data, projectId);
    await this.reloadData();
  };
  updateProgressStatus = async (entityId: number, data: any, projectId: number) => {
    await this.updateIdField(ActionField.progressStatus, entityId, data, projectId);
    await this.reloadData();
  };
  updateOwnerName = async (entityId: number, text: any, projectId: number) => {
    await this.updateIdField(ActionField.owner, entityId, text, projectId);
    await this.reloadData();
  };
}
